import { useEffect, useRef, useState } from "react"

import "./MatrixRain.css"

/* The left dock's ground, running (owner's ask, 2026-09-10): a rain of glyphs behind the roster,
   so the dock reads as a screen a machine is driving rather than a panel sitting still between
   ticks.

   **It is a background and it has to keep behaving like one.** The whole point of the ask was that
   it must not compete with what is on top of it, so three things hold it down and all three are
   deliberate: it draws under the roster's cards (only the dock's bare ground shows it, which means
   a nine-city empire hides most of it and a two-city one gets more — the panel dims itself as it
   fills), the intensity is one number in the stylesheet (`--rain-opacity`), and it runs at
   `RAIN_FPS` rather than at the monitor's rate, beside a canvas that is drawing a war.

   It reads nothing. No tick, no cities, no state — it takes no props and touches no store, so
   there is no chance of it becoming a second copy of a published number or a fog-of-war hole in a
   panel nothing redacts. `aria-hidden`, for the matching reason: there is nothing here to say. */

/* The glyphs are the terminal voice the app already speaks — the landing screen's "REMOTE SYSTEM ·
   UBUNTU 22.04" — rather than katakana, which is the film's alphabet and not this game's, and
   which the bundled mono face has no glyphs for (it would rain tofu boxes). 0 and 1 are repeated
   so the fall reads as mostly binary with hex and punctuation running through it. */
const GLYPHS = "0101010101ABCDEF0123456789<>/\\|+*#=$%&"

const CELL = 12
const FONT = "10px 'IBM Plex Mono', ui-monospace, monospace"
const RAIN_FPS = 12
const FRAME_MS = 1000 / RAIN_FPS
/* How much of the previous frame is rubbed out each tick. It fades to *transparent*
   (`destination-out`) rather than to a background colour, so the canvas stays see-through and the
   dock's own ground and texture show through the trails instead of being painted over. */
const FADE = 0.075
const HEAD_ALPHA = 0.6
// Rows per frame. A spread rather than one speed, or the columns fall as a single sheet.
const MIN_SPEED = 0.3
const MAX_SPEED = 0.85
// How eagerly a column that has run off the bottom starts again — low, so restarts stay staggered.
const RESTART_CHANCE = 0.02

const REDUCED_MOTION = "(prefers-reduced-motion: reduce)"

const glyph = () => GLYPHS[Math.floor(Math.random() * GLYPHS.length)]

const asksForStillness = () =>
  typeof window.matchMedia === "function" && window.matchMedia(REDUCED_MOTION).matches

const MatrixRain = () => {
  const canvas = useRef(null)
  const [still, setStill] = useState(asksForStillness)

  useEffect(() => {
    if (typeof window.matchMedia !== "function") return undefined
    const query = window.matchMedia(REDUCED_MOTION)
    const onChange = () => setStill(query.matches)
    query.addEventListener("change", onChange)
    return () => query.removeEventListener("change", onChange)
  }, [])

  useEffect(() => {
    const node = canvas.current
    if (still || !node) return undefined

    // jsdom has no 2D context, and neither does a browser that has run out of them.
    const ctx = typeof node.getContext === "function" ? node.getContext("2d") : null
    if (!ctx) return undefined

    let columns = 0
    let drops = []
    let speeds = []
    let rows = []

    /* The buffer is sized in CSS pixels, not device pixels. On a 2× screen that is a quarter of
       the fill each frame for a difference nobody can see at this opacity. */
    const measure = () => {
      const width = Math.max(1, Math.floor(node.clientWidth))
      const height = Math.max(1, Math.floor(node.clientHeight))
      if (node.width === width && node.height === height) return
      node.width = width
      node.height = height
      const wanted = Math.ceil(width / CELL)
      /* A new column starts anywhere over the dock's height, not above its top. Seeded above it
         the whole panel is empty for the first minute while the front falls into it, and the
         bottom half of a tall dock is bare ground the entire time somebody is looking at the
         opening rounds. Starting mid-fall means the dock is already raining when it appears. */
      const startBand = height / CELL + 20
      // Keep the columns that already exist, so a resize does not restart the whole fall.
      for (let i = columns; i < wanted; i += 1) {
        drops[i] = Math.random() * startBand - 20
        speeds[i] = MIN_SPEED + Math.random() * (MAX_SPEED - MIN_SPEED)
        rows[i] = -1
      }
      drops = drops.slice(0, wanted)
      speeds = speeds.slice(0, wanted)
      rows = rows.slice(0, wanted)
      columns = wanted
      ctx.font = FONT
      ctx.textBaseline = "top"
    }

    const paint = () => {
      const { width, height } = node
      const lastRow = height / CELL

      ctx.globalCompositeOperation = "destination-out"
      ctx.fillStyle = `rgba(0, 0, 0, ${FADE})`
      ctx.fillRect(0, 0, width, height)

      ctx.globalCompositeOperation = "source-over"
      ctx.fillStyle = `rgba(120, 236, 152, ${HEAD_ALPHA})`

      for (let i = 0; i < columns; i += 1) {
        drops[i] += speeds[i]
        const row = Math.floor(drops[i])
        // One glyph per cell entered, never per frame: a slow column redrawn every frame would
        // burn its head in far brighter than a fast one's.
        if (row !== rows[i]) {
          rows[i] = row
          if (row >= 0) ctx.fillText(glyph(), i * CELL, row * CELL)
        }
        if (drops[i] > lastRow && Math.random() < RESTART_CHANCE) {
          drops[i] = Math.random() * -10
          speeds[i] = MIN_SPEED + Math.random() * (MAX_SPEED - MIN_SPEED)
          rows[i] = -1
        }
      }
    }

    /* Force the first measure: `node.width` starts at the canvas default (300), which on a dock
       that happens to be 300 wide would match and leave the context without its font. */
    node.width = 0
    measure()

    let frame = null
    let previous = 0

    const loop = (now) => {
      frame = window.requestAnimationFrame(loop)
      if (now - previous < FRAME_MS) return
      previous = now
      measure()
      paint()
    }

    frame = window.requestAnimationFrame(loop)

    /* A dock that has just changed width is a canvas full of glyphs at the old width. The
       observer only marks it; the redraw happens on the next frame like everything else. */
    const observer =
      typeof ResizeObserver === "function"
        ? new ResizeObserver(() => {
            node.width = 0
          })
        : null
    if (observer) observer.observe(node)

    return () => {
      if (frame) window.cancelAnimationFrame(frame)
      if (observer) observer.disconnect()
    }
  }, [still])

  // Somebody who asked for "reduce motion" gets the plain ground the dock had before this existed.
  if (still) return null

  return <canvas className="matrix-rain" ref={canvas} aria-hidden="true" />
}

export default MatrixRain
